// El `reference` explícito es lo que hace que `import.meta.env` tipe cuando
// este archivo se compila desde el tsconfig de otro workspace: el .d.ts está en
// `src/`, que no entra en el `include` de quien nos importa.
/// <reference path="./vite-env.d.ts" />

// Helper functions para parsear variables de entorno
const parseEmptyString = (value: string | undefined): string => {
  if (!value) {
    return '';
  }

  return value.toString();
};

export const parseString = (value: string | undefined): string => {
  return (value ?? '').toString();
};

export const parseNumber = (value: string | undefined): number => {
  return Number(value ?? 0);
};

export const parseBoolean = (value: string | boolean | undefined): boolean => {
  if (!value) {
    return false;
  }

  if (value === 'true' || value === true) {
    return true;
  }

  return false;
};

// Tipo para configuraciones específicas de cada app
export interface AppSpecificConfig {
  sentryDsn?: string;
  [key: string]: unknown;
}

// Configuración compartida para todas las apps
export const createConfig = (appConfig?: AppSpecificConfig) => ({
  env: parseEmptyString(import.meta.env.VITE_ENV),
  isProduction: import.meta.env.VITE_ENV === 'production',
  isLocal:
    import.meta.env.VITE_ENV === 'local' ||
    import.meta.env.VITE_ENV === 'development',

  sessionKey: parseEmptyString(import.meta.env.VITE_SESSION_KEY),
  tasksApiKey: parseEmptyString(import.meta.env.VITE_TASKS_API_KEY),
  serviceApiKey: parseEmptyString(import.meta.env.VITE_SERVICE_API_KEY),

  // WebSocket URL
  wsUrl:
    parseEmptyString(import.meta.env.VITE_WS_URL) ||
    'wss://ws.yugiohargentina.com',

  useMixpanelAnalytics: parseBoolean(
    import.meta.env.VITE_USE_MIXPANEL_ANALYTICS
  ),
  mixpanelToken: parseEmptyString(import.meta.env.VITE_MIXPANEL_TOKEN),

  // URLs con fallbacks a producción
  apiUrl:
    parseEmptyString(import.meta.env.VITE_API_URL) ||
    'https://api.yugiohargentina.com',
  folderUrl:
    parseEmptyString(import.meta.env.VITE_FOLDER_URL) ||
    'https://folder.yugiohargentina.com',
  calendarUrl:
    parseEmptyString(import.meta.env.VITE_CALENDAR_URL) ||
    'https://calendario.yugiohargentina.com',
  rankingUrl:
    parseEmptyString(import.meta.env.VITE_RANKING_URL) ||
    'https://ranking.yugiohargentina.com',
  tournamentAppUrl:
    parseEmptyString(import.meta.env.VITE_TOURNAMENT_APP_URL) ||
    'https://torneos.yugiohargentina.com',
  webUrl:
    parseEmptyString(import.meta.env.VITE_WEB_URL) ||
    'https://yugiohargentina.com',
  marketplaceUrl:
    parseEmptyString(import.meta.env.VITE_MARKETPLACE_URL) ||
    'https://market.yugiohargentina.com',
  streamingUrl:
    parseEmptyString(import.meta.env.VITE_STREAMING_URL) ||
    'https://streaming.yugiohargentina.com',

  // Sentry: permite override específico por app o usa variable de entorno compartida
  sentryDsn:
    appConfig?.sentryDsn ?? parseEmptyString(import.meta.env.VITE_SENTRY_DSN),
  sentryEnabled: parseBoolean(import.meta.env.VITE_SENTRY_ENABLED),

  defaultStoreId: 43, // YGO Argentina default store id
  defaultOrganizerId: 3, // YGO Argentina default organizer id

  // AWS bucket con fallback
  awsBucket:
    parseEmptyString(import.meta.env.VITE_AWS_URL) ||
    'https://s3-us-west-2.amazonaws.com/static.yugiohargentina.com',

  // Contact email con fallback
  contactEmail:
    parseEmptyString(import.meta.env.VITE_CONTACT_EMAIL) ||
    'contacto@yugiohargentina.com',

  // Mobile app store URLs
  appStoreUrl: parseEmptyString(import.meta.env.VITE_APP_STORE_URL) || '',
  playStoreUrl: parseEmptyString(import.meta.env.VITE_PLAY_STORE_URL) || '',

  // Merge cualquier configuración adicional específica de la app
  ...appConfig,
});

export type Config = ReturnType<typeof createConfig>;
